import {
  MINOR_UNITS_PER_UNIT,
  formatCurrency,
  loyaltyDiscountFor,
  loyaltyPointsEarnedFor,
} from "@/lib/utils";

interface LoyaltyExampleProps {
  pointsPerUnit: string;
  redemptionRate: string;
  redemptionValue: string;
  currencySymbol: string;
}

const SAMPLE_SALE = 10 * MINOR_UNITS_PER_UNIT;
const SAMPLE_BALANCE_MULTIPLE = 2.5;

export function LoyaltyExample({
  pointsPerUnit,
  redemptionRate,
  redemptionValue,
  currencySymbol,
}: LoyaltyExampleProps) {
  const perUnit = parseFloat(pointsPerUnit) || 0;
  const rate = parseFloat(redemptionRate) || 0;
  const value = parseFloat(redemptionValue) || 0;

  const money = (amount: number) => formatCurrency(amount, currencySymbol);

  const earned = loyaltyPointsEarnedFor(SAMPLE_SALE, perUnit);
  const oneRateWorth = loyaltyDiscountFor(rate, rate, value);

  const sampleBalance = Math.round(rate * SAMPLE_BALANCE_MULTIPLE);
  const sampleBalanceWorth = loyaltyDiscountFor(sampleBalance, rate, value);

  const spendToEarnOneRate =
    perUnit > 0 ? Math.ceil((rate / perUnit) * MINOR_UNITS_PER_UNIT) : 0;

  return (
    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
      <p className="font-medium text-amber-900">With these settings</p>
      <ul className="space-y-1 text-amber-800">
        <li>
          {earned > 0
            ? `A ${money(SAMPLE_SALE)} sale earns the customer ${earned} points.`
            : "Sales earn no points — set Points per Dollar above 0."}
        </li>
        <li>
          {oneRateWorth > 0
            ? `${rate} points takes ${money(oneRateWorth)} off an order.`
            : "Points cannot be redeemed — set Redemption Rate and Redemption Value above 0."}
        </li>
        {sampleBalanceWorth > 0 && (
          <li>
            A customer holding {sampleBalance} points has{" "}
            {money(sampleBalanceWorth)} to spend.
          </li>
        )}

        {spendToEarnOneRate > 0 && oneRateWorth > 0 && (
          <li>
            They spend {money(spendToEarnOneRate)} to earn {rate} points, so the
            scheme gives back{" "}
            {((oneRateWorth / spendToEarnOneRate) * 100).toFixed(1)}% of what
            they spend.
          </li>
        )}
      </ul>
    </div>
  );
}
